import { animated, config, useTransition } from '@react-spring/web';
import { ComponentType, ReactNode } from 'react';
import styled from 'styled-components';

import { IconCheck } from '../../common/icons/cdl/Check';
import { Spinner } from '../../common/Spinner/Spinner';
import { Box } from '../../common/ui/Box';
import { theme } from '../../common/ui/theme';
import { Text } from '../Text/Text';

import { ToastDismissButton } from './components/ToastDismissButton';

type ToastIcon = ComponentType<{ width?: number; height?: number; color?: string }>;
type ToastState = 'error' | 'loading' | 'success';

const ToastContentWrapper = styled.div<{ $state?: ToastState }>`
    display: flex;
    align-items: stretch;
    background-color: ${(props) =>
        props.$state === 'error' ? theme.colors.negative.emphasis : theme.colors.background.emphasis};
    color: ${theme.colors.foreground.onEmphasis};
    border-radius: ${theme.radii[4]};
    box-shadow: ${theme.shadows.large};
    overflow: hidden;
    z-index: ${theme.zIndex.toast};
`;

const ToastMessage = styled(Text)`
    flex: 1;
    min-width: 0;
`;

type BaseToastProps = {
    isVisible: boolean;
    children: string;
    onDismiss?: () => void;
};

type StateAndIcon = BaseToastProps & {
    state: 'error';
    Icon?: ToastIcon;
};

type StateWithoutIcon = BaseToastProps & {
    state: 'success' | 'loading';
    Icon?: never;
};

type DefaultToastProps = BaseToastProps & {
    state?: never;
    Icon?: ToastIcon;
};

type ToastProps = StateAndIcon | StateWithoutIcon | DefaultToastProps;

export const Toast = ({ isVisible, state, children, Icon, onDismiss }: ToastProps) => {
    let leadingVisual: ReactNode = null;

    if (state === 'loading') {
        leadingVisual = <Spinner color={theme.colors.foreground.onEmphasis} size={18} strokeWidth={4} />;
    } else if (state === 'success') {
        leadingVisual = <IconCheck width={20} height={20} color={theme.colors.foreground.onEmphasis} />;
    } else if (Icon) {
        leadingVisual = <Icon width={20} height={20} color={theme.colors.foreground.onEmphasis} />;
    }

    const transitions = useTransition(isVisible, {
        from: { y: +50, opacity: 0 },
        enter: { y: 0, opacity: 1 },
        leave: { opacity: 0 },
        config: {
            ...config.default,
            tension: 300,
            clamp: true,
        },
    });

    return transitions((style, show) => {
        return show ? (
            <animated.div style={{ ...style }}>
                <ToastContentWrapper $state={state}>
                    <Box
                        display="flex"
                        alignItems="center"
                        gap={theme.space[8]}
                        paddingX={theme.space[16]}
                        paddingY={theme.space[8]}
                    >
                        {leadingVisual}
                        <ToastMessage variant="small">{children}</ToastMessage>
                    </Box>
                    {onDismiss ? <ToastDismissButton onClick={onDismiss} /> : null}
                </ToastContentWrapper>
            </animated.div>
        ) : null;
    });
};
